import React, { useEffect, useState } from "react";
import styles from "./OrderDetail.module.scss";
import TitleUpdater from "../../../../components/common/title/TitleUpdater";
import { useTranslation } from "react-i18next";
import { useLocalizedNavigate } from "../../../../hooks/useLocalizedNavigate";
import { useParams } from "react-router-dom";
import ProductsService from "../../../../api/services/products.service";
import { OrdersTypes } from "../../../../api/types/products.types";
import { formatDate } from "../../../../utils/date";
import { useLanguage } from "../../../../context/LanguageContext";
import Loading from "../../../../components/common/loading/Loading";

const OrderDetail = () => {
  const { t } = useTranslation("translation");
  const navigate = useLocalizedNavigate();
  const { id } = useParams<{ id: string }>();
  const [orderData, setOrderData] = useState<OrdersTypes | null>(null);
  const { currentLanguage } = useLanguage();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const getData = async () => {
      try {
        const res = await ProductsService.getAllOrders(1, 100);
        const foundOrder = res.data.find(
          (order: OrdersTypes) => order.payment.order_id === id,
        );
        setOrderData(foundOrder || null);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    getData();
  }, [id]);

  const getLocalizedName = (product: any) => {
    if (!product) return "";

    switch (currentLanguage) {
      case "az":
        return product.name_az || product.name_en;
      case "de":
        return product.name_de || product.name_en;
      case "es":
        return product.name_es || product.name_en;
      case "en":
      default:
        return product.name_en;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "1":
        return "#3498db";
      case "2":
        return "#f1c40f";
      case "3":
        return "#f1c40f";
      case "4":
        return "#e67e22";
      case "5":
        return "#e67e22";
      case "6":
        return "#2ecc71";
      default:
        return "#999999";
    }
  };

  const getStatusText = (status: string) => {
    return t(`order_status.${status}`) || t("order_status.1");
  };

  const maskCardNumber = (cardNumber: string) => {
    if (!cardNumber) return "";
    const cleaned = cardNumber.replace(/\s/g, "");
    return `**** **** **** ${cleaned.slice(-4)}`;
  };

  if (loading) {
    return <Loading />;
  }

  if (!orderData) {
    return (
      <div className={styles.order_detail}>
        <div className="container">
          <div className={styles.not_found}>
            <h2>{t("my_orders.no_orders")}</h2>
            <button
              className={styles.back_btn}
              onClick={() => navigate("/orders")}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M19 12H5" />
                <path d="m12 19-7-7 7-7" />
              </svg>
              {t("order_detail.back_to_orders")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { payment, orders: orderList } = orderData;
  const totalItems = orderList.reduce((sum, item) => sum + item.quantity, 0);
  const calculatedTotal = orderList.reduce((sum, item) => {
    const price = item.product_variant?.price || 0;
    return sum + item.quantity * price;
  }, 0);
  const firstOrderWithAddress: any = orderList.find((o: any) => o?.address);
  const deliveryAddress: any = firstOrderWithAddress?.address;
  const deliveryPhone: string | undefined =
    firstOrderWithAddress?.phone || payment?.user?.phone;
  const deliveryZipCode: string | undefined = firstOrderWithAddress?.zip_code;

  return (
    <div className={styles.order_detail}>
      <div className="container">
        <TitleUpdater title={t("order_detail.title")} />

        <button className={styles.back_btn} onClick={() => navigate("/orders")}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M19 12H5" />
            <path d="m12 19-7-7 7-7" />
          </svg>
          {t("order_detail.back_to_orders")}
        </button>

        <div className={styles.header}>
          <div className={styles.header_left}>
            <h1 className={styles.title}>{t("order_detail.title")}</h1>
            <p className={styles.order_id}>
              {t("order_detail.order_id")}: <span>{payment.order_id}</span>
            </p>
          </div>
          <div className={styles.header_right}>
            <div className={styles.total_badge}>
              <span className={styles.label}>{t("order_detail.total")}</span>
              <span className={styles.amount}>
                {calculatedTotal.toFixed(2)} {payment.currency_name}
              </span>
            </div>
          </div>
        </div>

        <div className={styles.content}>
          <div className={styles.left_section}>
            <div className={styles.info_card}>
              <h3 className={styles.card_title}>
                {t("order_detail.order_summary")}
              </h3>
              <div className={styles.info_grid}>
                <div className={styles.info_item}>
                  <span className={styles.label}>
                    {t("order_detail.order_date")}
                  </span>
                  <span className={styles.value}>
                    {formatDate(payment.created)}
                  </span>
                </div>
                <div className={styles.info_item}>
                  <span className={styles.label}>
                    {t("basket.order_summary.title")}
                  </span>
                  <span className={styles.value}>
                    {totalItems} {t("basket.order_summary.products_count")}
                  </span>
                </div>
                <div className={styles.info_item}>
                  <span className={styles.label}>
                    {t("order_detail.transaction_id")}
                  </span>
                  <span className={styles.value}>{payment.transaction_id}</span>
                </div>
                <div className={styles.info_item}>
                  <span className={styles.label}>
                    {t("order_detail.card_number")}
                  </span>
                  <span className={styles.value}>
                    {maskCardNumber(payment.card_number)}
                  </span>
                </div>
              </div>
            </div>

            <div className={styles.info_card}>
              <h3 className={styles.card_title}>
                {t("order_detail.payment_method")}
              </h3>
              <div className={styles.payment_info}>
                <div className={styles.payment_icon}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="2" y="5" width="20" height="14" rx="2" />
                    <line x1="2" y1="10" x2="22" y2="10" />
                  </svg>
                </div>
                <div className={styles.payment_details}>
                  <p className={styles.payment_type}>
                    {payment.currency_name} {t("order_detail.card_number")}
                  </p>
                  <p className={styles.payment_number}>
                    {maskCardNumber(payment.card_number)}
                  </p>
                </div>
              </div>
            </div>

            <div className={styles.info_card}>
              <h3 className={styles.card_title}>
                {t("order_detail.delivery_address")}
              </h3>

              {deliveryAddress ? (
                <div className={styles.address_info}>
                  <div className={styles.address_icon}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="32"
                      height="32"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                  </div>
                  <div className={styles.address_details}>
                    {deliveryAddress?.address && (
                      <p className={styles.address_line}>
                        <strong>{t("order_detail.address")}:</strong>{" "}
                        {deliveryAddress.address}
                      </p>
                    )}
                    {(deliveryAddress?.city?.name ||
                      deliveryAddress?.city?.country?.name) && (
                      <p className={styles.address_line}>
                        <strong>{t("order_detail.city")}:</strong>{" "}
                        {[
                          deliveryAddress?.city?.name,
                          deliveryAddress?.city?.country?.name,
                        ]
                          .filter(Boolean)
                          .join(", ")}
                      </p>
                    )}
                    {deliveryZipCode && (
                      <p className={styles.address_line}>
                        <strong>{t("order_detail.zip_code")}:</strong>{" "}
                        {deliveryZipCode}
                      </p>
                    )}
                    {deliveryPhone && (
                      <p className={styles.address_line}>
                        <strong>{t("order_detail.phone")}:</strong>{" "}
                        {deliveryPhone}
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <p className={styles.address_empty}>
                  {t("order_detail.no_delivery_address")}
                </p>
              )}
            </div>
          </div>

          <div className={styles.right_section}>
            <div className={styles.products_card}>
              <h3 className={styles.card_title}>
                {t("order_detail.order_items")}
              </h3>
              <div className={styles.products_list}>
                {orderList.map((item) => (
                  <div key={item.id} className={styles.product_item}>
                    <div className={styles.product_image}>
                      <img
                        src={item.product_variant.images[0]?.image || ""}
                        alt={getLocalizedName(item.product_variant)}
                      />
                    </div>
                    <div className={styles.product_info}>
                      <h4 className={styles.product_name}>
                        {getLocalizedName(item.product_variant)}
                      </h4>
                      <div className={styles.product_meta}>
                        <span className={styles.quantity}>
                          {t("order_detail.quantity")}: {item.quantity}
                        </span>
                        <span
                          className={styles.status}
                          style={{
                            color: getStatusColor(item.status),
                            borderColor: getStatusColor(item.status),
                          }}
                        >
                          {getStatusText(item.status)}
                        </span>
                      </div>
                      {item.product_variant.price && (
                        <p className={styles.product_price}>
                          {item.product_variant.price} {payment.currency_name}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetail;
